import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import IMedicalCertificate from '../../interfaces/IMedicalCertificate';
import moment from 'moment';
import { FaRegEdit } from 'react-icons/fa';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import { Download } from '@mui/icons-material';
import { useMedicalCertificateService } from '../../hooks/services/useMedicalCertificateService';
import { EVENTOS_WIDTH, getBaseCellProps, getStickyProps, getStickyRightProps, HeaderConfig } from '../../hooks/util/useStickTable';
import { hasAccessToAction } from '../../util/hasAccessTo';
import UserContext from '../../context/user/UserContext';

interface IRowProps {
  medicalCertificateRow: IMedicalCertificate;
  handleClick: (medicalCertificate: IMedicalCertificate) => void;
  seeCID: boolean;
  headers: HeaderConfig[];
}
interface DownloadResource {
  medicalCertificateid: string;
  filename: string;
  token: string;
  description: string;
}

interface Attachment {
  id: string;
  medicalCertificateId: string;
  attachment: string;
}

const FollowupListRow = (props: IRowProps, indx: number) => {
  const [openEvent, setOpenEvent] = React.useState(false);
  const [error, setError] = useState<string | null>(null);
  const medicalCertificateRequest = useMedicalCertificateService();
  const medicalCertificateRow = props.medicalCertificateRow;
  const { state } = useContext(UserContext);

  const canView = hasAccessToAction(
    ["followup-atestado-web"],
    state.resourcesWithActions,
    "Editar"
  );

  const getFilename = (attachment: Attachment) => {
    const url = attachment.attachment;
    const fileName = url.split('/')[url.split('/').length - 1];
    return fileName.substring(33, 999);
  };

  const handleDownloadAll = async () => {
    const attachments = medicalCertificateRow?.attachments || [];
    const parsedStorage = JSON.parse(sessionStorage.getItem('user') || '{}');
    const token = parsedStorage.access_token || '';

    for (const att of attachments) {
      if (!att.attachment) continue;

      try {
        const resource: DownloadResource = {
          medicalCertificateid: att.medicalCertificateId,
          filename: att.attachment,
          token: token,
          description: getFilename(att),
        };

        await handleDownload(resource);

      } catch (error) {
        setError(error instanceof Error ? error.message : 'Ocorreu um erro ao baixar o arquivo.');
      }

    }
  };

  const handleDownload = async (attachment: DownloadResource) => {
    try {
      const result = await medicalCertificateRequest.getDownloadImage(
        attachment.medicalCertificateid,
        attachment.filename,
        attachment.token,
        attachment.description,
      );
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.description
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao baixar o arquivo.');
    }
  };

  return (
    <>
      <TableRow key={`${indx}`}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Célula "Eventos" (sticky à esquerda) */}
        <TableCell
          sx={{
            ...getBaseCellProps(),
            position: 'sticky',
            left: 0,
            zIndex: 2,
            width: EVENTOS_WIDTH,
            minWidth: EVENTOS_WIDTH,
          }}
        >
          <IconButton aria-label="expand row" size="small" onClick={() => setOpenEvent(!openEvent)}>
            {openEvent ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {props.headers.map((head, index) => (
          <TableCell key={head.field} sx={getStickyProps(props.headers, index)}>
            {head.render
              ? head.render(medicalCertificateRow)
              : (medicalCertificateRow as Record<string, any>)[head.field] ?? ''}
          </TableCell>
        ))}

        <TableCell sx={getBaseCellProps()}>
          {medicalCertificateRow?.alerts?.length !== 0 ? 'Sim' : 'Não'}
        </TableCell>

        <TableCell sx={getStickyRightProps()}>
          <Tooltip title="Editar" placement="left">
            <IconButton onClick={() => props.handleClick(medicalCertificateRow)}>
              <FaRegEdit />
            </IconButton>
          </Tooltip>
          {medicalCertificateRow?.attachments && medicalCertificateRow?.attachments?.length > 0 && (
            <Tooltip title={`Baixar ${medicalCertificateRow.attachments.length} anexo(s)`} placement="left">
              <IconButton onClick={handleDownloadAll} size="medium" disabled={!canView}>
                <Download />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>

      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={openEvent} timeout="auto" unmountOnExit>
            <Box sx={{ margin: '10px 0px 20px 0px', padding: '0px 10px 0px 10px', width: '100%' }}>
              <Typography variant="h6" gutterBottom component="div">
                Eventos
              </Typography>
              <Table size="small" aria-label="events">
                <TableHead>
                  <TableRow>
                    <TableCell width={'10%'}>Data</TableCell>
                    <TableCell width={'80%'}>Descrição</TableCell>
                    <TableCell>Responsável</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parseInt(`${medicalCertificateRow.events?.length}`) > 0 ? (
                    medicalCertificateRow.events?.map(event => (
                      <TableRow key={event.id} hover={true}>
                        <TableCell width={'10%'} sx={{ whiteSpace: 'nowrap' }}>
                          {moment(event.createdAt).isValid() ? moment(event.createdAt).format('DD/MM/yyyy HH:mm') : ''}
                        </TableCell>
                        <TableCell width={'80%'}>{event.description}</TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{event.username}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{ padding: 1, maxWidth: '100vw' }}
                        >
                          <SearchIcon sx={{ marginRight: 2 }} />
                          <Box>Sem eventos para listar</Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default FollowupListRow;
